import { map, values, mapObjIndexed, flatten } from "ramda";
import numeral from "numeral";
import "numeral/locales/de";

import i18n from "@/i18n";
import reportHeader from "@/utils/pdf/header";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import fundInfo from "@/utils/pdf/fundInfo";
import { getDisclaimer } from "@/utils/getDisclaimer";
import markdownToPdfmake from "@fng/common/markdownToPdfmake";

const h1 = (text) => ({
  text,
  bold: true,
  fontSize: 18,
  margin: [0, 0, 0, 18],
  pageBreak: "before",
});

const h2 = (text) => ({
  text,
  bold: true,
  fontSize: 14,
  margin: [0, 0, 0, 10],
});

const h3 = (text) => ({
  text,
  bold: true,
  fontSize: 12,
  margin: [0, 0, 0, 5],
});

const summary = (fund, audit) => {
  const result = [];

  result.push(h2(i18n.t("reportSummary.minimal", fund.language)));

  result.push(
    fund.report.minimalRequirementsResult === "PASS"
      ? {
          text: i18n.t("fund.report.minimalRequirementsPassed", fund.language),
          margin: [0, 0, 0, 20],
        }
      : {
          stack: markdownToPdfmake(fund.report.fundResult.minimalRequirementsText),
          margin: [0, 0, 0, 20],
        }
  );

  const audited = fund.report.minimalRequirementsResult === "PASS";

  if (!(audited && fund.report.stepModelCategories)) return result;

  result.push(h2(i18n.t("reportSummary.step", fund.language)));

  result.push({
    stack: markdownToPdfmake(fund.report.fundResult.stepModelRequirementsText),
    margin: [0, 0, 0, 10],
  });

  const header = [
    i18n.t("reportSummary.finalResult", fund.language),
    `${fund.report.stars} ${i18n.tc("report.stars", fund.report.stars, fund.language)}`,
  ];

  if (audit) {
    header.push("");
    header.push(numeral(fund.report.perc).format("0%"));
  }

  const body = [
    header,
    ...map((category) => {
      const row = [
        i18n.t(`stepModelRequirementCategory.${category.category}.name`, fund.language),
        `${category.stars} ${i18n.tc("report.stars", category.stars, fund.language)}`,
      ];

      if (audit) {
        row.push(`${category.points} / ${category.maxPoints}`);
        row.push(numeral(category.perc).format("0%"));
      }

      return row;
    }, fund.report.categoriesWithStars),
  ];

  const widths = audit ? ["*", 50, 50, 50] : ["*", 200];

  result.push({
    style: "requirementTable",
    table: {
      widths,
      body,
    },
    margin: [0, 0, 0, 20],
  });

  return result;
};

const minimalRequirements = (fund, requirements, audit) => {
  const categories = groupByCategory(requirements);

  const header = h1(i18n.t("fund.report.minimalRequirements.title", fund.language));

  const widths = audit ? ["*", "*", 30] : ["*", "*"];

  const cats = values(
    mapObjIndexed((category, index) => {
      return [
        h3(i18n.t(`minimalRequirementCategory.${index}.name`, fund.language)),
        {
          style: "requirementTable",
          margin: [0, 0, 0, 20],
          table: {
            widths,
            body: map((requirement) => {
              const row = [
                [
                  [
                    {
                      text: i18n.t(`minimalRequirement.${requirement.requirementId}.name`, fund.language),
                      bold: true,
                    },
                    {
                      text: i18n.t(`minimalRequirement.${requirement.requirementId}.description`, fund.language),
                      bold: false,
                    },
                  ],
                ],
                markdownToPdfmake(requirement.result.description),
              ];

              if (audit) row.push(requirement.result.result);

              return row;
            }, category),
          },
        },
      ];
    }, categories)
  );

  return [header, ...cats];
};

const stepModelRequirements = (fund, categories, audit) => {
  const audited = fund.report.minimalRequirementsResult === "PASS";

  const header = h1(i18n.t("fund.report.stepModelRequirements.title", fund.language));

  const cats = map((category) => {
    let result = [h3(i18n.t(`stepModelRequirementCategory.${category.category}.name`, fund.language))];

    if (category.weight) {
      result.push({
        text: i18n.tc(`report.category.finalResult`, fund.language, category.stars, {
          stars: category.stars,
        }),
        bold: true,
        margin: [0, 0, 0, 5],
      });
    }

    const requirements = map((requirement) => {
      const row = [
        [
          {
            text: i18n.t(`stepModelRequirement.${requirement.requirementId}.name`, fund.language),
            bold: true,
          },
          {
            text: i18n.t(`stepModelRequirement.${requirement.requirementId}.description`, fund.language),
            bold: false,
          },
        ],
        audited
          ? markdownToPdfmake(requirement.result.description)
          : i18n.t(`fund.report.requirementNotAudited`, fund.language),
      ];

      if (audit) row.push(requirement.result.points || "");

      return row;
    }, category.requirements);

    const widths = audit ? ["*", "*", 30] : ["*", "*"];

    const table = {
      style: "requirementTable",
      table: {
        widths,
        body: requirements.length ? requirements : [[]],
      },
      margin: [0, 0, 0, 20],
    };

    return [result, table];
  }, categories);

  return [header, cats];
};

const flattenStepModelCategories = (categories) =>
  flatten(
    map(
      (category) => (category.children.length ? [category, flattenStepModelCategories(category.children)] : category),
      categories
    )
  );

const generateReportPdfData = (fund, type, audit = false) => {
  numeral.locale(fund.language);

  const provisional =
    type === "provisional"
      ? {
          text: i18n.t("fund.report.provisionalInfo"),
          bold: true,
          fontSize: 20,
          alignment: "center",
          color: "red",
        }
      : null;

  const name = {
    text: fund.reportTitle
      ? fund.reportTitle
      : i18n.t(`fund.report.title.final`, fund.language, {
          year: fund.auditPeriod.year + 1,
        }),
    bold: true,
    fontSize: 20,
    alignment: "center",
  };

  let result = minimalRequirements(fund, fund.report.minimalRequirements, audit);

  if (fund.report.stepModelCategories) {
    result = result.concat(
      stepModelRequirements(fund, flattenStepModelCategories(fund.report.stepModelCategories), audit)
    );
  }

  const disclaimer = {
    text: getDisclaimer(fund),
    fontSize: 6,
    color: "#888888",
    margin: [40, 10, 40, 20],
  };

  return {
    footer: (currentPage, pageCount) => [
      {
        text: `${currentPage.toString()}/${pageCount.toString()}`,
        alignment: "center",
        margin: [0, 20, 0, 0],
      },
      disclaimer,
    ],
    content: [provisional, name, reportHeader, fundInfo(fund), summary(fund, audit), result],
    defaultStyle: {
      fontSize: 8,
    },
    pageMargins: [40, 40, 40, 110],
  };
};

export default generateReportPdfData;
